import React from "react";
import Head from "next/head";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import {
  createStyles,
  Theme,
  makeStyles,
  useTheme,
} from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import { useQuery, gql } from "@apollo/client";

import AppBar from "@Components/appBar/appBar";
import SummaryCard from "@Components/posts/summaryCard";
import HeaderBlock from "@Components/posts/headerBlock";
import Section from "@Components/posts/section";
import Footer from "@Components/common/footer";
import client from "@Config/graphCMSInit";
import SEO from "@Components/common/seo";
import DisplayAd from "@Components/ads/displayAd";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonContainer: {
      margin: "24px 0",
      width: "100%",
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
    },
  })
);

const GET_WIKIS = gql`
  query GetWIkis($index: WikiIndexEnum!) {
    wikis(where: { index: $index }) {
      slug
      name
      kanaName
      images(first: 1) {
        url(
          transformation: {
            image: { resize: { width: 300, height: 300, fit: clip } }
            document: { output: { format: webp } }
          }
        )
      }
      description
    }
  }
`;

interface KanaItem {
  index: string;
  ja: string;
  active: boolean;
}

const kanaArray: KanaItem[] = [
  { index: "a", ja: "あ", active: true },
  { index: "ka", ja: "か", active: true },
  { index: "sa", ja: "さ", active: true },
  { index: "ta", ja: "た", active: true },
  { index: "na", ja: "な", active: true },
  { index: "ha", ja: "は", active: true },
  { index: "ma", ja: "ま", active: true },
  { index: "ya", ja: "や", active: true },
  { index: "ra", ja: "ら", active: true },
  { index: "wa", ja: "わ", active: false },
];

export const getStaticPaths = () => {
  const array = kanaArray
    .filter((kana) => {
      return kana.active;
    })
    .map((kana) => {
      return { params: { page: kana.index } };
    });

  return {
    paths: array,
    fallback: false,
  };
};

export const getStaticProps = async ({ locale, params }: any) => {
  const index = params.page as string;

  const cmsQuery = await client.query({
    query: GET_WIKIS,
    variables: { index },
  });

  // console.log(params, cmsQuery.data);

  const current = kanaArray.find((kana) => {
    return kana.index === index;
  });

  return {
    props: {
      current,
      data: cmsQuery.data,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  };
};

interface StaticProps {
  current: KanaItem | undefined;
  data: {
    wikis: {
      slug: string;
      name: string;
      kanaName: string;
      images: {
        url: string;
      }[];
      description: string;
    }[];
  };
}

export default function Home({ current, data }: StaticProps) {
  const { t: t_common } = useTranslation("common");
  const classes = useStyles();
  const theme = useTheme();

  let wikis = [...data.wikis];
  const sortedWikis = wikis.sort((x, y) => {
    return x.kanaName.localeCompare(y.kanaName, "ja");
  });

  return (
    <React.Fragment>
      <SEO
        pageTitle="用語解説"
        pageImg={sortedWikis ? sortedWikis[0]?.images[0]?.url : undefined}
      />

      <AppBar>
        <Section>
          {sortedWikis ? (
            <React.Fragment>
              <HeaderBlock
                title={`用語解説: ${current?.ja || ""}`}
                subtitle="長年培われてきた日本の建築技術は、旅館ではいまも様々なところに見ることができます。
                住宅の更新が進み、日常では見ることのない部分も多いでしょう。
                そして、旅館でしか登場しない単語もあります。
                ここでは、動画や写真に登場するたくさんの専門用語を解説します。"
              />

              <div className={classes.buttonContainer}>
                <ButtonGroup
                  variant="text"
                  color="primary"
                  aria-label="text primary button group"
                >
                  {kanaArray.map((kana, i) => {
                    return (
                      <Button
                        key={i}
                        disabled={!kana.active}
                        href={`/wiki/${kana.index}`}
                      >
                        {kana.ja}
                      </Button>
                    );
                  })}
                </ButtonGroup>
              </div>

              <Grid container spacing={3}>
                {sortedWikis.map((item, i) => {
                  return (
                    <Grid item sm={6} xs={12} key={i} id={item.slug}>
                      <SummaryCard
                        title={item.name}
                        subtitle={item.kanaName}
                        description={item.description}
                        image={item.images[0]?.url}
                      />
                    </Grid>
                  );
                })}

                <Grid item sm={6} xs={12} key="a">
                  <DisplayAd variant="square" />
                </Grid>
              </Grid>
            </React.Fragment>
          ) : null}
        </Section>

        <Footer />
      </AppBar>
    </React.Fragment>
  );
}
